import React, { useState } from 'react'
import { forwardRef } from '@grupoboticario/flora-react-system'
import { AlertProps, CloseButtonProps } from './types'
import {
  StyledAlert,
  StyledButton,
  StyledActionButton,
  StyledDescription,
  StyledFlex,
  StyledActionsContainer,
} from './styles'
import {
  CheckCircleFillIcon,
  CrossHexagonFillIcon,
  CrossIcon,
  ExclamationMarkTriangleFillIcon,
  InformationCircleFillIcon,
} from '@grupoboticario/flora-react-icons'
import { Box, Flex, FloraButtonProps } from '@grupoboticario/flora-react'

const iconStyled = { flexShrink: 0 }
const icons = {
  success: <CheckCircleFillIcon style={iconStyled} size={24} />,
  error: <CrossHexagonFillIcon style={iconStyled} size={24} />,
  warning: <ExclamationMarkTriangleFillIcon style={iconStyled} size={24} />,
  info: <InformationCircleFillIcon style={iconStyled} size={24} />,
}

const buttonStyleMap: Record<
  Exclude<AlertProps['status'], undefined>,
  FloraButtonProps['styleSemantic'] | 'info'
> = {
  error: 'warning',
  info: 'info',
  success: 'success',
  warning: 'alert',
}

const CloseButton = ({ onClick, status, color }: CloseButtonProps) => {
  return (
    <StyledButton
      onClick={onClick}
      status={status}
      color={color}
      aria-label="Fechar"
      data-testid="button-close-alert"
    >
      <CrossIcon size={16} />
    </StyledButton>
  )
}

export const Alert = forwardRef<'div', AlertProps>(
  (
    {
      title,
      description,
      'autotagging-flora': autotaggingFlora = 'off',
      'data-flora-text': dataFloraText,
      'data-autotagging-flora-section': dataAutotaggingFloraSection = '',
      status = 'success',
      color = 'light',
      primaryButton,
      secondaryButton,
      size = 'default',
      hideCloseButton,
      hideIcon,
      onClose,
      ...rest
    },
    forwardedRef,
  ) => {
    const [isOpen, setIsOpen] = useState(true)

    if (!isOpen) {
      return null
    }

    const buttonStyleSemantic = buttonStyleMap[status]
    const commonButtonProps = {
      status,
      color,
      size: 'small',
      surfaceColor: color,
      styleSemantic: buttonStyleSemantic,
    }

    const isSmallSize = size === 'small'

    // Hide close button by default on small variant that has buttons
    if (hideCloseButton === undefined && isSmallSize && (primaryButton || secondaryButton)) {
      hideCloseButton = true
    }

    const closeButton = (
      <CloseButton
        status={status}
        color={color}
        onClick={() => {
          setIsOpen(false)
          onClose?.()
        }}
      />
    )

    return (
      <StyledAlert
        ref={forwardedRef}
        data-flora="alert"
        data-testid="alert"
        autotagging-flora={autotaggingFlora}
        data-flora-text={dataFloraText ?? title}
        data-autotagging-flora-section={dataAutotaggingFloraSection}
        status={status}
        color={color}
        size={size}
        role="alert"
        {...rest}
      >
        <StyledFlex size={size}>
          <Flex align="center" gap="$2">
            {!hideIcon && icons[status ?? 'success']}
            {!isSmallSize && title}
          </Flex>

          {!hideCloseButton && !isSmallSize && closeButton}
        </StyledFlex>

        <StyledDescription>{description}</StyledDescription>

        {(primaryButton || secondaryButton) && (
          <StyledActionsContainer size={size}>
            {primaryButton && (
              <StyledActionButton
                {...commonButtonProps}
                css={{ mr: '$4' }}
                hierarchy={primaryButton.hierarchy}
                hierarchyVariant={primaryButton.hierarchy}
                onClick={primaryButton.onClick}
              >
                {primaryButton.children}
              </StyledActionButton>
            )}

            {secondaryButton && (
              <StyledActionButton
                {...commonButtonProps}
                hierarchy={secondaryButton.hierarchy}
                hierarchyVariant={secondaryButton.hierarchy}
                onClick={secondaryButton.onClick}
              >
                {secondaryButton.children}
              </StyledActionButton>
            )}
          </StyledActionsContainer>
        )}

        {!hideCloseButton && isSmallSize && <Box css={{ ml: '$4' }}>{closeButton}</Box>}
      </StyledAlert>
    )
  },
)
